import { Card } from '@monorepo/base/src/components/card/card';
import { observer } from 'mobx-react';
import styles from './bidder-targeting.module.scss';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../../stores';
import { FormError } from '@monorepo/base/src/components/form/form-error/form-error';
import { ButtonGroup, GButton } from '@monorepo/base/src/components/buttons/group/button-group';
import { bidderCampaignTypeToTitle, CampaignTargetingLinkChoicePolicy } from '../../../../models/bidder-campaign/bidder-campaign.types';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Statuses } from '../../../../enums/status';
import { constantCase, sentenceCase } from 'change-case';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { BidderCampaignTargetingLinkModel } from '../../../../models/bidder-campaign/bidder-campaign-targeting-link.model';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { Fragment, useState } from 'react';
import { TargetingLinksTable } from './bidder-targeting-links-table';
import { DeviceTypes } from '@monorepo/tools/src/lib/enums/device-types';
import { DropdownNew } from '@monorepo/base/src/components/dropdown-new/dropdown-new';
import { PrimaryText } from '@monorepo/base/src/components/buttons/buttons';
import { Dropdown } from '@monorepo/base/src/components/dropdown/dropdown';

type StatusFilter = 'all' | 'active' | 'paused' | 'cache';
type GroupBy = 'none' | 'publisher' | 'status' | 'cache';

const STATUS_OPTIONS = ['All', 'Active', 'Paused', 'Paused in cache'];
const GROUP_BY_OPTIONS = ['None', 'Publisher', 'Status', 'Paused in cache'];

function statusFilterFromLabel(label: string): StatusFilter {
	if (label === 'Active') {
		return 'active';
	}
	if (label === 'Paused') {
		return 'paused';
	}
	if (label === 'Paused in cache') {
		return 'cache';
	}
	return 'all';
}

function groupByFromLabel(label: string): GroupBy {
	if (label === 'Publisher') {
		return 'publisher';
	}
	if (label === 'Status') {
		return 'status';
	}
	if (label === 'Paused in cache') {
		return 'cache';
	}
	return 'none';
}

function groupByToLabel(groupBy: GroupBy): string {
	if (groupBy === 'publisher') {
		return 'Publisher';
	}
	if (groupBy === 'status') {
		return 'Status';
	}
	if (groupBy === 'cache') {
		return 'Paused in cache';
	}
	return 'None';
}

function matchesLinkFilter(
	link: BidderCampaignTargetingLinkModel,
	search: string,
	statusFilter: StatusFilter,
	publisherNameByPid: Record<string, string>
): boolean {
	const q = search.trim().toLowerCase();
	if (q) {
		const publisherName = link.pid != null && link.pid !== '' ? publisherNameByPid[String(link.pid)] : undefined;
		const haystack = [link.yid, link.dest_link, publisherName, String(link.pid ?? ''), link.rh_pid]
			.filter(Boolean)
			.join(' ')
			.toLowerCase();
		if (!haystack.includes(q)) {
			return false;
		}
	}
	if (statusFilter === 'active') {
		return link.status === Statuses.Active;
	}
	if (statusFilter === 'paused') {
		return link.status === Statuses.Paused;
	}
	if (statusFilter === 'cache') {
		return link.paused_in_cache === 1;
	}
	return true;
}

type LinkGroup = {
	key: string;
	title: string;
	links: BidderCampaignTargetingLinkModel[];
};

function groupLinks(links: BidderCampaignTargetingLinkModel[], groupBy: GroupBy): LinkGroup[] {
	if (groupBy === 'none') {
		return links.length ? [{ key: 'all', title: '', links }] : [];
	}

	const buckets = new Map<string, { title: string; links: BidderCampaignTargetingLinkModel[] }>();

	links.forEach(link => {
		let key: string;
		let title: string;

		if (groupBy === 'publisher') {
			const rhPid = link.rh_pid?.trim() ? link.rh_pid.trim() : '';
			key = rhPid || 'none';
			title = rhPid ? `RH Publisher - (${rhPid})` : 'No RH publisher';
		} else if (groupBy === 'status') {
			const isActive = link.status === Statuses.Active;
			key = isActive ? 'active' : 'paused';
			title = isActive ? 'Active' : 'Paused';
		} else {
			const inCache = link.paused_in_cache === 1;
			key = inCache ? 'cache' : 'not-cache';
			title = inCache ? 'Paused in cache' : 'Not paused in cache';
		}

		const bucket = buckets.get(key);
		if (bucket) {
			bucket.links.push(link);
		} else {
			buckets.set(key, { title, links: [link] });
		}
	});

	return Array.from(buckets.entries()).map(([key, { title, links: groupLinksList }]) => ({
		key,
		title: `${title} · ${groupLinksList.length} link${groupLinksList.length === 1 ? '' : 's'}`,
		links: groupLinksList,
	}));
}

export const BidderTargeting = observer(() => {
	const { bidderStore } = useStores<IAdminxStore>();
	const bidderCrudStore = bidderStore.getCrud();
	const inputOptions = bidderStore.getInputOptions();
	const formStore = bidderCrudStore.getFormStore();
	const campaign = bidderCrudStore.getData().campaign;
	const targetings = campaign.campaign_targeting || [];
	const targetingPolicy = campaign.campaign_targeting_link_choice_policy;

	const [linkSearch, setLinkSearch] = useState('');
	const [linkStatusFilter, setLinkStatusFilter] = useState<StatusFilter>('all');
	const [linkGroupBy, setLinkGroupBy] = useState<GroupBy>(() =>
		campaign.campaign_targeting_link_choice_policy === CampaignTargetingLinkChoicePolicy.PerPublisher ? 'publisher' : 'none'
	);

	const publisherNamesMapById = bidderStore.publishersList.getData()?.publishers.reduce(
		(acc, publisher) => {
			acc[publisher.id] = publisher.name;
			return acc;
		},
		{} as Record<id, string>
	);

	const publisherNameByPid: Record<string, string> = {
		...(publisherNamesMapById ? Object.fromEntries(Object.entries(publisherNamesMapById).map(([k, v]) => [String(k), v])) : {}),
		...(campaign.campaign_publisher_lists?.white_list?.reduce(
			(acc, publisher) => {
				acc[String(publisher.pid)] = publisher.publisher_name;
				return acc;
			},
			{} as Record<string, string>
		) || {}),
	};

	return (
		<div className={styles.targetings}>
			<Card
				className={styles.targetingList}
				title={`Targetings`}
				id={'bidderTargetings'}
				icon={'target-05'}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'bidder_targeting_section')] }}>
				<Spacer />
				<ButtonGroup
					className={styles.performancePixelTypeWrapper}
					title={'Targeting link policy'}
					required
					defaultValue={targetingPolicy}
					error={formStore.getErrors().get('campaign_targeting_link_choice_policy')?.getMsg()}
					onChange={setValues => {
						const nextPolicy = setValues.values().next().value;
						campaign.campaign_targeting_link_choice_policy = nextPolicy as CampaignTargetingLinkChoicePolicy;
						if (nextPolicy === CampaignTargetingLinkChoicePolicy.PerPublisher) {
							setLinkGroupBy('publisher');
						}
						formStore.clearError('campaign_targeting_link_choice_policy');
					}}>
					<GButton
						text={bidderCampaignTypeToTitle.get(CampaignTargetingLinkChoicePolicy.PerPublisher)}
						value={CampaignTargetingLinkChoicePolicy.PerPublisher}
					/>
					<GButton
						text={bidderCampaignTypeToTitle.get(CampaignTargetingLinkChoicePolicy.ByWeight)}
						value={CampaignTargetingLinkChoicePolicy.ByWeight}
					/>
					<GButton
						text={bidderCampaignTypeToTitle.get(CampaignTargetingLinkChoicePolicy.WithConversionLimit)}
						value={CampaignTargetingLinkChoicePolicy.WithConversionLimit}
					/>
				</ButtonGroup>
				{/* <Spacer /> */}

				{targetings.map((targeting, index) => {
					const filteredLinks = (targeting.campaign_targeting_links || []).filter(link =>
						matchesLinkFilter(link, linkSearch, linkStatusFilter, publisherNameByPid)
					);
					const linkGroups = groupLinks(filteredLinks, linkGroupBy);
					const hasVisibleLinks = filteredLinks.length > 0;

					return (
						<Fragment key={index}>
							<Card
								collapsible
								className={styles.targetingItem}
								title={`Targeting - ${targeting.id || 'NEW'}`}
								titleChildren={
									<Toggle
										label={
											targeting.status === Statuses.Active
												? sentenceCase(Statuses.Active)
												: sentenceCase(Statuses.Paused)
										}
										onChange={status => {
											targeting.status = status ? Statuses.Active : Statuses.Paused;
										}}
										defaultValue={targeting.status === Statuses.Active}
										debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_status')] }}
									/>
								}
								defaultCollapsed={targetings.length > 1 && !!targeting.id}>
								<div className={styles.inputsWrapper}>
									<div className={styles.inputWrapper}>
										<Input
											type={'number'}
											className={`${styles.inputWithHint}`}
											value={targeting.estimated_rate ? targeting.estimated_rate?.toString() : '0'}
											placeholder={'Estimated Rate'}
											onValue={value => {
												targeting.estimated_rate = parseFloat(value);
												formStore.clearError('estimated_rate');
											}}
											debugProps={{ dataAttrs: [new DataAttribute('id', 'estimated_rate')] }}
										/>
										<FormError
											className={styles.fromError}
											msg={formStore.getErrors().get('estimatedRate')?.getMsg()}
										/>
									</div>
									<div className={styles.inputWrapper}>
										<Input
											type={'number'}
											className={`${styles.inputWithHint}`}
											value={targeting.q_estimated_rate ? targeting.q_estimated_rate?.toString() : '0'}
											placeholder={'Q Estimated Rate'}
											onValue={value => {
												targeting.q_estimated_rate = parseFloat(value);
												formStore.clearError('q_estimated_rate');
											}}
											debugProps={{ dataAttrs: [new DataAttribute('id', 'q_estimated_rate')] }}
										/>
										<FormError
											className={styles.fromError}
											msg={formStore.getErrors().get('qEstimatedRate')?.getMsg()}
										/>
									</div>
								</div>

								<ButtonGroup
									defaultValue={constantCase(targeting.device_type)}
									required
									title="Device Type"
									onChange={value => {
										targeting.device_type = constantCase(value.values().next().value as string);
										formStore.clearError('device_type');
									}}
									error={formStore.getErrors().get('device_type')?.getMsg()}>
									<GButton
										icon={'phone-android'}
										text={DeviceTypes.Mobile}
										value={constantCase(DeviceTypes.Mobile)}
										debugProps={{ dataAttrs: [new DataAttribute('id', 'device_types_mobile')] }}
									/>
									<GButton
										icon={'tablet-02'}
										text={DeviceTypes.Tablet}
										value={constantCase(DeviceTypes.Tablet)}
										debugProps={{ dataAttrs: [new DataAttribute('id', 'device_types_tablet')] }}
									/>
									<GButton
										icon={'monitor-01'}
										text={DeviceTypes.Computer}
										value={constantCase(DeviceTypes.Computer)}
										debugProps={{ dataAttrs: [new DataAttribute('id', 'device_types_computer')] }}
									/>
								</ButtonGroup>

								<DropdownNew
									classNames={{
										dropdownWrapper: `${styles.dropdown} ${styles.w100}`,
										listItem: styles.listItem,
										dropdownInput: `${styles.dropdownInput} ${styles.w100}`,
										autocompleteInputWrapper: styles.w100,
									}}
									defaultValues={targeting.os.split(',')}
									label="OS"
									required
									multi
									options={inputOptions
										.getOperatingSystems()
										.filter(i => i.to_target)
										.map(i => ({
											value: i.code,
											label: i.name,
										}))}
									onSelect={os => {
										targeting.os = os.filter(Boolean).join(',');
										formStore.clearError('os');
									}}
									error={formStore.getErrors().get('os')?.getMsg()}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'os')] }}
								/>

								<DropdownNew
									classNames={{
										dropdownWrapper: `${styles.dropdown} ${styles.w100}`,
										listItem: styles.listItem,
										dropdownInput: `${styles.dropdownInput} ${styles.w100}`,
										autocompleteInputWrapper: styles.w100,
									}}
									defaultValues={targeting.browser.split(',')}
									label="Browsers"
									required
									multi
									options={inputOptions
										.getBrowsers()
										.filter(i => i.to_target)
										.map(i => ({
											value: i.code,
											label: i.name,
										}))}
									onSelect={browsers => {
										targeting.browser = browsers.filter(Boolean).join(',');
										formStore.clearError('browser');
									}}
									error={formStore.getErrors().get('browser')?.getMsg()}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'browser')] }}
								/>

								<DropdownNew
									classNames={{
										dropdownWrapper: `${styles.dropdown} ${styles.w100}`,
										listItem: styles.listItem,
										dropdownInput: `${styles.dropdownInput} ${styles.w100}`,
										autocompleteInputWrapper: styles.w100,
									}}
									required
									multi
									defaultValues={targeting.country_code.split(',')}
									label="Geo"
									autocomplete
									autocompletePlaceholder="Search Country"
									options={inputOptions.getGeos().map(i => ({
										label: `${i.name} - (${i.code})`,
										value: i.code,
									}))}
									onSelect={geos => {
										targeting.country_code = geos.filter(Boolean).join(',');
										formStore.clearError('country_code');
									}}
									error={formStore.getErrors().get('country_code')?.getMsg()}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'country_code')] }}
									isLabelOnBorder
								/>

								<DropdownNew
									classNames={{
										dropdownWrapper: `${styles.dropdown} ${styles.w100}`,
										listItem: styles.listItem,
										dropdownInput: `${styles.dropdownInput} ${styles.w100}`,
										autocompleteInputWrapper: styles.w100,
									}}
									defaultValues={targeting.excluded_browser.split(',')}
									label="Excluded Browsers"
									required
									multi
									options={inputOptions.getBrowsers().map(i => ({
										value: i.code,
										label: i.name,
									}))}
									onSelect={browsers => {
										targeting.excluded_browser = browsers.filter(Boolean).join(',');
										formStore.clearError('excluded_browser');
									}}
									error={formStore.getErrors().get('excluded_browser')?.getMsg()}
									debugProps={{ dataAttrs: [new DataAttribute('id', 'excluded_browser')] }}
								/>

								<Spacer />
								<div className={styles.linksSectionTitle}>Targeting Links</div>

								<div className={styles.linksToolbar}>
									<Dropdown
										className={styles.statusDropdown}
										label="Status"
										options={STATUS_OPTIONS}
										defaultOption="All"
										onSelect={val => {
											const label = val ? [...val][0] : 'All';
											setLinkStatusFilter(statusFilterFromLabel(label || 'All'));
										}}
										isCloseIcon={false}
									/>
									<Dropdown
										key={linkGroupBy}
										label="Group by"
										options={GROUP_BY_OPTIONS}
										defaultOption={groupByToLabel(linkGroupBy)}
										onSelect={val => {
											const label = val ? [...val][0] : 'None';
											setLinkGroupBy(groupByFromLabel(label || 'None'));
										}}
										isCloseIcon={false}
									/>
									<div>
										<Input
											placeholder="Search links"
											value={linkSearch}
											onValue={value => {
												setLinkSearch(value);
											}}
											className={styles.linksToolbarSearch}
										/>
									</div>
								</div>

								{!hasVisibleLinks ? <div className={styles.linksEmptyState}>No links match filters</div> : null}

								{linkGroupBy === 'none' ? (
									<TargetingLinksTable
										campaignId={campaign.campaign_id}
										targeting={targeting}
										links={filteredLinks}
										policy={targetingPolicy}
									/>
								) : (
									linkGroups.map(group => (
										<Card className={styles.targetingLinkWrapper} collapsible title={group.title} key={group.key}>
											<Spacer />
											<TargetingLinksTable
												campaignId={campaign.campaign_id}
												targeting={targeting}
												links={group.links}
												policy={targetingPolicy}
											/>
										</Card>
									))
								)}

								<PrimaryText icon="plus" onClick={() => targeting.addNewLink()}>
									Add Targeting Link
								</PrimaryText>
							</Card>

							{targetings.length > index + 1 && <Spacer />}
						</Fragment>
					);
				})}

				<Spacer />

				<PrimaryText icon="plus" onClick={() => campaign.addTargeting()}>
					Add Targeting
				</PrimaryText>
			</Card>
		</div>
	);
});
